import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBillingV1TaxRegion from './billing-v1-tax-region-tax-regions.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

/**
 * A tax region uses a location to define a specific tax treatment.
 * A location is defined by `country` and `subdivision`. The tax region also
 * dictates whether or not tax is included in the displayed price.
 */
export interface TaxRegion {
  /**
   * Tax region ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * 2-letter country code in [ISO-3166 alpha-1](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.
   * @format COUNTRY
   * @minLength 2
   */
  country?: string;
  /**
   * Subdivision (such as state, prefecture, or province) in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.
   * @maxLength 50
   */
  subdivision?: string;
  /**
   * The tax calculator ID to use to calculate tax for this region.
   *
   * Call List Tax Calculators to retrieve a list of available calculators for a site.
   * @format GUID
   */
  appId?: string;
  /** Whether tax is included in the price. */
  taxIncludedInPrice?: boolean;
  /**
   * Revision number, which increments by 1 each time the tax region is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the tax region.
   *
   * Ignored when creating a tax region.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the tax region was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the tax region was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface CreateTaxRegionRequest {
  /** Tax region to create. */
  taxRegion: TaxRegion;
}

export interface CreateTaxRegionResponse {
  /** Created tax region. */
  taxRegion?: TaxRegion;
}

export interface ConflictErrorDetails {
  /**
   * Conflict details.
   * @maxLength 1000
   */
  reasonForConflict?: string;
}

export interface BulkCreateTaxRegionRequest {
  /**
   * Tax regions to create.
   * @minSize 1
   * @maxSize 100
   */
  taxRegions?: TaxRegion[];
  /**
   * Whether to return the full tax region objects in the response.
   *
   * Default: `true`
   */
  returnEntity?: boolean;
}

export interface BulkCreateTaxRegionResponse {
  /**
   * Tax regions created by bulk action.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkCreateTaxRegionResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateTaxRegionResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /**
   * Tax region.
   *
   * This field is returned if the operation was successful and
   * `returnEntity` is set to `true`.
   */
  item?: TaxRegion;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetTaxRegionRequest {
  /**
   * ID of the tax region to retrieve.
   * @format GUID
   */
  taxRegionId: string;
}

export interface GetTaxRegionResponse {
  /** Retrieved tax region. */
  taxRegion?: TaxRegion;
}

export interface UpdateTaxRegionRequest {
  /** Tax region info to update. */
  taxRegion: TaxRegion;
}

export interface UpdateTaxRegionResponse {
  /** Updated tax region. */
  taxRegion?: TaxRegion;
}

export interface DeleteTaxRegionRequest {
  /**
   * ID of the tax region to delete.
   * @format GUID
   */
  taxRegionId: string;
}

export interface DeleteTaxRegionResponse {}

export interface QueryTaxRegionsRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryTaxRegionsResponse {
  /** Retrieved tax regions. */
  taxRegions?: TaxRegion[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface CreateOrGetTaxRegionWithoutValidationRequest {
  /** Tax region to create. */
  taxRegion?: TaxRegion;
}

export interface CreateOrGetTaxRegionWithoutValidationResponse {
  /** Created tax region. */
  taxRegion?: TaxRegion;
}

export interface DeleteTaxRegionWithoutDomainEventsRequest {
  /**
   * ID of the tax region to delete.
   * @format GUID
   */
  taxRegionId?: string;
}

export interface DeleteTaxRegionWithoutDomainEventsResponse {}

export interface BulkRestoreTaxRegionsRequest {
  /**
   * Tax region IDs to restore.
   * @format GUID
   * @minSize 1
   * @maxSize 200
   */
  taxRegionIds?: string[];
}

export interface BulkRestoreTaxRegionsResponse {
  /**
   * Restored tax regions result
   * @minSize 1
   * @maxSize 200
   */
  results?: BulkRestoreTaxRegionsResult[];
}

export interface BulkRestoreTaxRegionsResult {
  itemMetadata?: ItemMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountDetails {
  /**
   * ID of the account.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the site, if applicable.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateTaxRegionApplicationErrors =
  | {
      code?: 'ALREADY_EXISTS';
      description?: string;
      data?: ConflictErrorDetails;
    }
  | {
      code?: 'CALCULATOR_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CALCULATOR_IS_NOT_SUPPORTED_FOR_THIS_TAX_REGION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_SUBDIVISION_FORMAT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SUBDIVISIONS_NOT_SUPPORTED_FOR_COUNTRY';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateTaxRegionApplicationErrors =
  | {
      code?: 'CALCULATOR_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CALCULATOR_IS_NOT_SUPPORTED_FOR_THIS_TAX_REGION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_SUBDIVISION_FORMAT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SUBDIVISIONS_NOT_SUPPORTED_FOR_COUNTRY';
      description?: string;
      data?: Record<string, any>;
    };

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

export interface TaxRegionCreatedEnvelope {
  entity: TaxRegion;
  metadata: EventMetadata;
}

/** @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.TAX_REGION_READ
 * @webhook
 * @eventType wix.billing.v1.tax_region_created
 * @serviceIdentifier wix.billing.v1.TaxRegionsService
 * @slug created
 * @documentationMaturity preview
 */
export declare function onTaxRegionCreated(
  handler: (event: TaxRegionCreatedEnvelope) => void | Promise<void>
): void;

export interface TaxRegionDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a tax region is deleted.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.TAX_REGION_READ
 * @webhook
 * @eventType wix.billing.v1.tax_region_deleted
 * @serviceIdentifier wix.billing.v1.TaxRegionsService
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onTaxRegionDeleted(
  handler: (event: TaxRegionDeletedEnvelope) => void | Promise<void>
): void;

export interface TaxRegionUpdatedEnvelope {
  entity: TaxRegion;
  metadata: EventMetadata;
}

/**
 * Triggered when a tax region is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.TAX_REGION_READ
 * @webhook
 * @eventType wix.billing.v1.tax_region_updated
 * @serviceIdentifier wix.billing.v1.TaxRegionsService
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onTaxRegionUpdated(
  handler: (event: TaxRegionUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a tax region.
 *
 * Tax regions require a tax calculator `appId`. Call List Tax Calculators to retrieve a list of available calculators for a site.
 *
 * Wix uses tax regions to calculate tax.
 * <!-- Subdivisions are supported for the following countries: `AU`, `BR`, `CA`, `FR`, `DE`, `IN`, `IT`, `MX`, `NL`, `ES`, `AE`, `GB`, `US` -->
 * @param taxRegion - Tax region to create.
 * @public
 * @documentationMaturity preview
 * @requiredField taxRegion
 * @requiredField taxRegion.appId
 * @requiredField taxRegion.country
 * @permissionId BILLING.TAX_REGION_CREATE
 * @applicableIdentity APP
 * @returns Created tax region.
 * @fqn wix.billing.v1.TaxRegionsService.CreateTaxRegion
 */
export async function createTaxRegion(
  taxRegion: NonNullablePaths<TaxRegion, `appId` | `country`, 2>
): Promise<
  NonNullablePaths<
    TaxRegion,
    `country` | `subdivision` | `appId` | `taxIncludedInPrice`,
    2
  > & {
    __applicationErrorsType?: CreateTaxRegionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    taxRegion: taxRegion,
  });

  const reqOpts = ambassadorWixBillingV1TaxRegion.createTaxRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.taxRegion!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { taxRegion: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['taxRegion']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a tax region.
 * @param taxRegionId - ID of the tax region to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField taxRegionId
 * @permissionId BILLING.TAX_REGION_READ
 * @applicableIdentity APP
 * @returns Retrieved tax region.
 * @fqn wix.billing.v1.TaxRegionsService.GetTaxRegion
 */
export async function getTaxRegion(
  taxRegionId: string
): Promise<
  NonNullablePaths<
    TaxRegion,
    `country` | `subdivision` | `appId` | `taxIncludedInPrice`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    taxRegionId: taxRegionId,
  });

  const reqOpts = ambassadorWixBillingV1TaxRegion.getTaxRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.taxRegion!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { taxRegionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['taxRegionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a tax region.
 *
 * Each time the tax region is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the tax region.
 * This ensures you're working with the latest tax region and prevents
 * unintended overwrites.
 * @param _id - Tax region ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField taxRegion
 * @requiredField taxRegion.revision
 * @param taxRegion - Tax region info.
 * @permissionId BILLING.TAX_REGION_UPDATE
 * @applicableIdentity APP
 * @returns Updated tax region.
 * @fqn wix.billing.v1.TaxRegionsService.UpdateTaxRegion
 */
export async function updateTaxRegion(
  _id: string,
  taxRegion: NonNullablePaths<UpdateTaxRegion, `revision`, 2>
): Promise<
  NonNullablePaths<
    TaxRegion,
    `country` | `subdivision` | `appId` | `taxIncludedInPrice`,
    2
  > & {
    __applicationErrorsType?: UpdateTaxRegionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    taxRegion: { ...taxRegion, id: _id },
  });

  const reqOpts = ambassadorWixBillingV1TaxRegion.updateTaxRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.taxRegion!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { taxRegion: '$[1]' },
        explicitPathsToArguments: { 'taxRegion.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'taxRegion']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateTaxRegion {
  /**
   * Tax region ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * 2-letter country code in [ISO-3166 alpha-1](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.
   * @format COUNTRY
   * @minLength 2
   */
  country?: string;
  /**
   * Subdivision (such as state, prefecture, or province) in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.
   * @maxLength 50
   */
  subdivision?: string;
  /**
   * The tax calculator ID to use to calculate tax for this region.
   *
   * Call List Tax Calculators to retrieve a list of available calculators for a site.
   * @format GUID
   */
  appId?: string;
  /** Whether tax is included in the price. */
  taxIncludedInPrice?: boolean;
  /**
   * Revision number, which increments by 1 each time the tax region is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the tax region.
   *
   * Ignored when creating a tax region.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the tax region was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the tax region was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

/**
 * Deletes a tax region.
 *
 * When a tax region is deleted, tax is not calculated and zero tax will be returned for addresses in this region.
 * @param taxRegionId - ID of the tax region to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField taxRegionId
 * @permissionId BILLING.TAX_REGION_DELETE
 * @applicableIdentity APP
 * @fqn wix.billing.v1.TaxRegionsService.DeleteTaxRegion
 */
export async function deleteTaxRegion(taxRegionId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    taxRegionId: taxRegionId,
  });

  const reqOpts = ambassadorWixBillingV1TaxRegion.deleteTaxRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { taxRegionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['taxRegionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of tax regions.
 *
 * The `queryTaxRegions()` function builds a query to retrieve a list of tax regions and returns a `TaxRegionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `TaxRegionsQueryBuilder` functions onto the query. `TaxRegionsQueryBuilder` functions enable you to sort, filter, and control the results that `queryTaxRegions()` returns.
 *
 * `queryTaxRegions()` runs with the following `TaxRegionsQueryBuilder` default that you can override:
 * + `ascending("_id")`
 *
 * The functions that are chained to `queryTaxRegions()` are applied in the order they are called. For example, if you apply `ascending("country")` and then `ascending("subdivision")`, the results are sorted first by the `"country"`, and then, if there are multiple results with the same `"country"`, the items are sorted by `"subdivision"`.
 *
 * The following `TaxRegionsQueryBuilder` functions are supported for the `queryTaxRegions()` function. For a full description of the tax region object, see the object returned for the `items` property in `TaxRegionsQueryResult`.
 * @public
 * @documentationMaturity preview
 * @permissionId BILLING.TAX_REGION_READ
 * @applicableIdentity APP
 * @fqn wix.billing.v1.TaxRegionsService.QueryTaxRegions
 */
export function queryTaxRegions(): TaxRegionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    TaxRegion,
    'CURSOR',
    QueryTaxRegionsRequest,
    QueryTaxRegionsResponse
  >({
    func: async (payload: QueryTaxRegionsRequest) => {
      const reqOpts = ambassadorWixBillingV1TaxRegion.queryTaxRegions(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryTaxRegionsRequest['query']) => {
      const args = [query, {}] as [QueryTaxRegionsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryTaxRegionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.taxRegions,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface TaxRegionsQueryResult extends QueryCursorResult {
  items: TaxRegion[];
  query: TaxRegionsQueryBuilder;
  next: () => Promise<TaxRegionsQueryResult>;
  prev: () => Promise<TaxRegionsQueryResult>;
}

export interface TaxRegionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => TaxRegionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => TaxRegionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => TaxRegionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => TaxRegionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => TaxRegionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => TaxRegionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'country' | 'subdivision' | 'appId',
    value: string
  ) => TaxRegionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: any[]
  ) => TaxRegionsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => TaxRegionsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => TaxRegionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => TaxRegionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'country'
      | 'subdivision'
      | 'appId'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => TaxRegionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => TaxRegionsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => TaxRegionsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<TaxRegionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.billing.v1.TaxRegionsService.QueryTaxRegions
 * @requiredField query
 */
export async function typedQueryTaxRegions(
  query: TaxRegionQuery
): Promise<
  NonNullablePaths<
    QueryTaxRegionsResponse,
    | `taxRegions`
    | `taxRegions.${number}.country`
    | `taxRegions.${number}.subdivision`
    | `taxRegions.${number}.appId`
    | `taxRegions.${number}.taxIncludedInPrice`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixBillingV1TaxRegion.queryTaxRegions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface TaxRegionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'appId',
        'country',
        'subdivision'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  TaxRegion,
  TaxRegionQuerySpec
>;
export type TaxRegionQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};
